import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { LoadingSpinner } from '@/components/features/LoadingSpinner'
import { LineBasedInvoicePageContent } from '@/components/features/invoicing/LineBasedInvoicePageContent'
import { CreditNoteModeCards } from '@/components/features/invoicing/CreditNoteModeCards'
import { InvoiceSelectField } from '@/components/features/invoicing/InvoiceSelectField'
import { InvoiceSummaryCard } from '@/components/features/invoicing/InvoiceSummaryCard'
import { CreditPreviewBlock } from '@/components/features/invoicing/CreditPreviewBlock'
import { SupersessionWarningAlert } from '@/components/features/invoicing/SupersessionWarningAlert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getProjectById, getProjectInvoices } from '@/lib/api/projects'
import { validatePartialAmountTtc, type VatSubtotal } from '@/lib/creditNoteHelpers'
import type { CreditNoteMode, ProjectInvoice } from '@/types'

type PartialInputMode = 'ttc' | 'percent'

// SOLY-CN-002 — state shape that round-trips between CreditNotePage and
// InvoicePreviewPage. Raw form inputs (partialAmount/partialInputMode) live
// here so the form rehydrates verbatim on Retour; partialAmountTtc/
// partialProportion are the BE-facing computed fields.
interface CreditNoteLocationState {
  mode?: CreditNoteMode
  referencedInvoiceIds?: string[]
  partialAmountTtc?: number
  partialProportion?: number
  partialAmount?: string
  partialInputMode?: PartialInputMode
  // SOLY-CN-003 — optional free-text reason, round-tripped with the preview.
  reason?: string
}

export default function CreditNotePage() {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // SOLY-CN-002 — rehydrate from /preview back-navigation if available.
  const initialState = (location.state as CreditNoteLocationState | null) ?? null

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalInvoiced, setTotalInvoiced] = useState(0)
  const [projectInvoices, setProjectInvoices] = useState<ProjectInvoice[]>([])
  const [defaultVatPercent, setDefaultVatPercent] = useState<number | undefined>(undefined)

  // Mode picker state.
  const [mode, setMode] = useState<CreditNoteMode>(initialState?.mode ?? 'full_cancellation')

  // Mode A + B shared state (single referenced invoice).
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | undefined>(
    initialState?.referencedInvoiceIds?.[0],
  )

  // Mode B state.
  const [partialInputMode, setPartialInputMode] = useState<PartialInputMode>(
    initialState?.partialInputMode ?? 'ttc',
  )
  const [partialAmount, setPartialAmount] = useState<string>(initialState?.partialAmount ?? '')

  // SOLY-CN-003 — optional free-text reason (Mode A/B), surfaced on the PDF.
  const [reason, setReason] = useState<string>(initialState?.reason ?? '')

  // Dialog state.
  const [showTabSwitchConfirm, setShowTabSwitchConfirm] = useState(false)
  const [pendingMode, setPendingMode] = useState<CreditNoteMode | null>(null)

  useEffect(() => {
    if (!projectId) return
    const load = async () => {
      try {
        const [data, project] = await Promise.all([
          getProjectInvoices(projectId),
          getProjectById(projectId),
        ])
        setTotalInvoiced(data.project.totalInvoiced)
        setProjectInvoices(data.invoices)
        if (project?.vatRate != null) {
          setDefaultVatPercent(Math.round(project.vatRate * 100))
        }
      } catch {
        setError('Erreur lors du chargement des données de facturation')
      } finally {
        setIsLoading(false)
      }
    }
    void load()
  }, [projectId])

  const selectedInvoice = useMemo(
    () => projectInvoices.find((i) => i.id === selectedInvoiceId),
    [projectInvoices, selectedInvoiceId],
  )

  // Mode B local preview reconstructs single-rate subtotals from the listing
  // DTO (amountWithTax + vatPercents). For multi-rate sources the BE remains
  // authoritative — the preview is illustrative; the persisted NC uses the
  // exact handler computation.
  const sourceSubtotals: VatSubtotal[] = useMemo(() => {
    if (!selectedInvoice || !selectedInvoice.vatPercents?.length) return []
    if (selectedInvoice.vatPercents.length === 1) {
      const rate = selectedInvoice.vatPercents[0]
      const ttc = selectedInvoice.amountWithTax
      const ht = Math.round((ttc / (1 + rate / 100)) * 100) / 100
      return [{ vatPercent: rate, taxableAmount: ht, vatAmount: ttc - ht, totalWithTax: ttc }]
    }
    // Multi-rate fallback: even split (illustrative only).
    const ttcPerRate = selectedInvoice.amountWithTax / selectedInvoice.vatPercents.length
    return selectedInvoice.vatPercents.map((rate) => {
      const ht = Math.round((ttcPerRate / (1 + rate / 100)) * 100) / 100
      return { vatPercent: rate, taxableAmount: ht, vatAmount: ttcPerRate - ht, totalWithTax: ttcPerRate }
    })
  }, [selectedInvoice])

  const partialTtcValue = useMemo(() => {
    if (!selectedInvoice) return 0
    const n = parseFloat(partialAmount.replace(',', '.'))
    if (!Number.isFinite(n)) return 0
    if (partialInputMode === 'ttc') return n
    return Math.round(((selectedInvoice.amountWithTax * n) / 100) * 100) / 100
  }, [partialInputMode, partialAmount, selectedInvoice])

  const partialError = useMemo(() => {
    if (!selectedInvoice || !partialAmount) return null
    if (partialInputMode === 'percent') {
      const n = parseFloat(partialAmount.replace(',', '.'))
      if (!Number.isFinite(n) || n <= 0 || n >= 100) {
        return 'Le pourcentage doit être strictement entre 0 et 100'
      }
      return null
    }
    return validatePartialAmountTtc(partialTtcValue, selectedInvoice.amountWithTax)
  }, [selectedInvoice, partialInputMode, partialAmount, partialTtcValue])

  const isCurrentModeDirty = (): boolean => {
    if (mode === 'full_cancellation') return Boolean(selectedInvoiceId)
    if (mode === 'partial_proportional') return Boolean(selectedInvoiceId) || partialAmount !== ''
    return false // free_lines dirty handled by LineBasedInvoicePageContent itself
  }

  const handleModeChange = (next: CreditNoteMode) => {
    if (next === mode) return
    if (isCurrentModeDirty()) {
      setPendingMode(next)
      setShowTabSwitchConfirm(true)
      return
    }
    setMode(next)
  }

  const confirmTabSwitch = () => {
    if (pendingMode) {
      setMode(pendingMode)
      setSelectedInvoiceId(undefined)
      setPartialAmount('')
    }
    setShowTabSwitchConfirm(false)
    setPendingMode(null)
  }

  // SOLY-CN-002 — navigate to /preview for Mode A and Mode B. The persisted
  // emission happens from the preview's "Générer la facture" CTA so the user
  // sees the actual PDF (consistent with deposit/final/standalone/direct/Mode C).
  const navigateToPreview = () => {
    if (!projectId || !selectedInvoice) return
    navigate(`/projets/${projectId}/invoices/preview`, {
      state: {
        invoiceType: 'credit_note',
        // Date-only ISO (YYYY-MM-DD) — matches the format the other invoice
        // flows pass via URL params, so the preview "Date" chip renders cleanly.
        invoiceDate: new Date().toISOString().slice(0, 10),
        lines: [],
        referencedInvoiceIds: [selectedInvoice.id],
        mode,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(mode === 'partial_proportional'
          ? {
              partialAmountTtc: partialTtcValue,
              // Raw FE inputs round-trip so Retour rehydrates the form verbatim.
              partialAmount,
              partialInputMode,
            }
          : {}),
      },
    })
  }

  // SOLY-CN-003 — shared reason field, rendered in both Mode A and Mode B
  // (defined once to stay DRY). Optional; trimmed before it reaches the BE.
  const reasonField = (
    <div className="space-y-1.5">
      <Label htmlFor="credit-note-reason">Motif (optionnel)</Label>
      <Textarea
        id="credit-note-reason"
        data-testid="credit-note-reason-input"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Ex. : Changement dénomination société"
        maxLength={500}
        rows={2}
      />
      <p className="text-xs text-muted-foreground">
        Affiché sur la note de crédit, pour la clarté en cas de contrôle.
      </p>
    </div>
  )

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Note de crédit</h1>
          <p className="text-sm text-muted-foreground">
            Choisissez le type d'annulation pour ce projet.
          </p>
        </div>

        <CreditNoteModeCards value={mode} onChange={handleModeChange} />

        {mode === 'full_cancellation' && (
          <div className="space-y-4">
            <Label>Facture à annuler</Label>
            <InvoiceSelectField
              invoices={projectInvoices}
              value={selectedInvoiceId}
              onChange={setSelectedInvoiceId}
              emptyAction={{
                label: '→ Note de crédit libre (sans facture source)',
                onClick: () => handleModeChange('free_lines'),
              }}
            />
            {selectedInvoice && (
              <>
                <InvoiceSummaryCard invoice={selectedInvoice} />
                <SupersessionWarningAlert invoiceNumber={selectedInvoice.number} />
                {reasonField}
                <Button
                  type="button"
                  data-testid="credit-note-mode-a-preview-btn"
                  className="w-full"
                  onClick={navigateToPreview}
                >
                  Aperçu PDF
                </Button>
              </>
            )}
          </div>
        )}

        {mode === 'partial_proportional' && (
          <div className="space-y-4">
            <Label>Facture à créditer partiellement</Label>
            <InvoiceSelectField
              invoices={projectInvoices}
              value={selectedInvoiceId}
              onChange={setSelectedInvoiceId}
              emptyAction={{
                label: '→ Note de crédit libre (sans facture source)',
                onClick: () => handleModeChange('free_lines'),
              }}
            />
            {selectedInvoice && (
              <>
                <InvoiceSummaryCard invoice={selectedInvoice} />

                <div className="space-y-2">
                  <Label>Saisir le montant</Label>
                  <RadioGroup
                    value={partialInputMode}
                    onValueChange={(v) => setPartialInputMode(v as PartialInputMode)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="ttc"
                        id="partial-ttc"
                        data-testid="credit-note-partial-input-toggle-ttc"
                      />
                      <Label htmlFor="partial-ttc" className="font-normal cursor-pointer">
                        Montant TTC
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="percent"
                        id="partial-percent"
                        data-testid="credit-note-partial-input-toggle-percent"
                      />
                      <Label htmlFor="partial-percent" className="font-normal cursor-pointer">
                        Pourcentage
                      </Label>
                    </div>
                  </RadioGroup>
                  <div className="relative">
                    <Input
                      type="text"
                      inputMode="decimal"
                      data-testid="credit-note-partial-amount-input"
                      value={partialAmount}
                      onChange={(e) => setPartialAmount(e.target.value)}
                      placeholder={partialInputMode === 'ttc' ? '0,00' : '0'}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                      {partialInputMode === 'ttc' ? '€' : '%'}
                    </span>
                  </div>
                </div>

                <CreditPreviewBlock
                  sourceSubtotals={sourceSubtotals}
                  sourceTtc={selectedInvoice.amountWithTax}
                  partialTtc={partialTtcValue}
                  errorMessage={partialError}
                  hideRateBreakdown={(selectedInvoice.vatPercents?.length ?? 0) > 1}
                />

                {reasonField}

                <Button
                  type="button"
                  data-testid="credit-note-mode-b-preview-btn"
                  className="w-full"
                  disabled={!partialAmount || partialError != null || partialTtcValue <= 0}
                  onClick={navigateToPreview}
                >
                  Aperçu PDF
                </Button>
              </>
            )}
          </div>
        )}

        {mode === 'free_lines' && (
          <LineBasedInvoicePageContent
            projectId={projectId ?? ''}
            title="Note de crédit libre"
            description="Saisie libre des lignes à créditer"
            remainingAmount={0}
            totalInvoiced={totalInvoiced}
            initialLines={[]}
            invoiceType="credit_note"
            error={error}
            projectInvoices={projectInvoices}
            defaultVatPercent={defaultVatPercent}
          />
        )}
      </div>

      {/* Tab-switch dirty confirmation */}
      <Dialog open={showTabSwitchConfirm} onOpenChange={setShowTabSwitchConfirm}>
        <DialogContent data-testid="credit-note-tab-switch-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Changer de type ?</DialogTitle>
            <DialogDescription>
              Vous avez des modifications non sauvegardées. Changer de type les supprimera.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTabSwitchConfirm(false)}>
              Annuler
            </Button>
            <Button
              data-testid="credit-note-tab-switch-confirm-btn"
              onClick={confirmTabSwitch}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
